import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Badge } from '../components/badges';
import { StatusPill } from '../components/match';
import { Topbar } from '../components/Shell';
import { Confirm, Empty, ErrorBox, Field, Loading, Seg, useAction } from '../components/ui';
import { del, get, patch, post, put, qs } from '../lib/api';
import { fmtAgo, fmtDateTime, fromLocalInput, STATUS_LABEL, toLocalInput } from '../lib/format';
import type { ArticleAdmin, Competition, GameSettings, GoalKind, MatchDetail, MatchStatus, Period, Round, Side } from '../lib/types';
import { ArticleState, KIND_LABEL as ARTICLE_KIND, useAiStatus } from './Articles';

/** The buttons that make sense from each status. Anything else is under "Force a status". */
const NEXT: Partial<Record<MatchStatus, [MatchStatus, string][]>> = {
  SCHEDULED: [['FIRST_HALF', 'Kick off'], ['POSTPONED', 'Postpone']],
  FIRST_HALF: [['HALF_TIME', 'Half-time'], ['SUSPENDED', 'Suspend']],
  HALF_TIME: [['SECOND_HALF', 'Start second half'], ['SUSPENDED', 'Suspend']],
  SECOND_HALF: [['FINISHED', 'Full time'], ['EXTRA_TIME', 'Extra time'], ['SUSPENDED', 'Suspend']],
  EXTRA_TIME: [['FINISHED', 'Full time (after extra time)'], ['PENALTIES', 'Penalty shoot-out'], ['SUSPENDED', 'Suspend']],
  PENALTIES: [['FINISHED', 'Full time']],
  SUSPENDED: [['FIRST_HALF', 'Resume first half'], ['SECOND_HALF', 'Resume second half'], ['FINISHED', 'Full time'], ['ABANDONED', 'Abandon']],
  POSTPONED: [['SCHEDULED', 'Reschedule']],
};

const PERIOD_LABEL: Record<Period, string> = { FIRST_HALF: 'First half', SECOND_HALF: 'Second half', EXTRA_TIME: 'Extra time', PENALTIES: 'Shoot-out' };
const KIND_LABEL: Record<GoalKind, string> = { GOAL: 'Goal', PENALTY_GOAL: 'Penalty', OWN_GOAL: 'Own goal' };

export function MatchDetailPage() {
  const { matchId } = useParams({ strict: false }) as { matchId: string };
  const id = Number(matchId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = ['match', id];
  const q = useQuery({ queryKey: key, queryFn: () => get<MatchDetail>(`/admin/matches/${id}`), refetchInterval: 15_000 });
  const { busy, run } = useAction();
  const [confirm, setConfirm] = useState<null | 'void' | 'delete' | { status: MatchStatus; label: string }>(null);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 20_000); // keep the live minute moving
    return () => clearInterval(t);
  }, []);

  if (q.isLoading) return <Loading />;
  if (q.error) return <div className="page"><ErrorBox error={q.error} /></div>;
  const d = q.data!;
  const m = d.match;
  const apply = async (fn: () => Promise<unknown>, ok: string) => {
    const r = await run(fn, ok);
    if (r && typeof r === 'object' && 'match' in (r as object)) qc.setQueryData(key, r);
    await qc.invalidateQueries({ queryKey: key });
    await qc.invalidateQueries({ queryKey: ['matches'] });
    await qc.invalidateQueries({ queryKey: ['dashboard'] });
    return r;
  };
  const statusTo = (status: MatchStatus, note?: string) => apply(() => post(`/admin/matches/${id}/status`, { status, note }), `Status: ${STATUS_LABEL[status]}`);
  const started = !!m.kickedOffAt;
  const destructive = (s: MatchStatus) => ['ABANDONED', 'CANCELLED', 'AWARDED', 'POSTPONED', 'FINISHED'].includes(s);

  return (
    <>
      <Topbar
        title={`${m.homeName} v ${m.awayName}`}
        sub={
          <span className="row" style={{ gap: 6 }}>
            <Badge spec={m.competitionBadge} size={16} /> {m.competitionName} · {m.roundName ?? 'no round'} · {fmtDateTime(m.kickoffAt)}
            {!m.kickoffConfirmed && <span className="pill pill-amber">Time TBC</span>}
            {!m.published && <span className="pill">Draft</span>}
          </span>
        }
        actions={
          <>
            <Link to="/matches/$matchId/data" params={{ matchId: String(id) }} className="btn">
              Match data
            </Link>
            <button type="button" className="btn" disabled={busy} onClick={() => apply(() => post(`/admin/matches/${id}/rescore`), 'Re-scored')}>
              Re-score now
            </button>
            <button type="button" className={m.published ? 'btn' : 'btn btn-primary'} disabled={busy} onClick={() => apply(() => post('/admin/matches/publish', { ids: [id], published: !m.published }), m.published ? 'Unpublished' : 'Published')}>
              {m.published ? 'Unpublish' : 'Publish'}
            </button>
          </>
        }
      />
      <div className="page">
        <div className="cols cols-main">
          <div className="stack" style={{ gap: 20 }}>
            {/* Scoreboard and status */}
            <section className="card" aria-label="Match state">
              <div className="card-body stack" style={{ gap: 18 }}>
                <div className="scoreboard">
                  <div className="side">
                    <Badge spec={m.homeBadge} size={64} />
                    {m.homeName}
                  </div>
                  <div className="stack" style={{ alignItems: 'center', gap: 6 }}>
                    <span className="big num">{m.rtHome === null ? '–' : `${m.rtHome} – ${m.rtAway}`}</span>
                    {m.aetHome !== null && <span className="muted num">After extra time {m.aetHome} – {m.aetAway}</span>}
                    {m.penHome !== null && <span className="muted num">Penalties {m.penHome} – {m.penAway}</span>}
                    {m.htHome !== null && <span className="muted small num">Half-time {m.htHome} – {m.htAway}</span>}
                    <StatusPill m={m} />
                  </div>
                  <div className="side">
                    <Badge spec={m.awayBadge} size={64} />
                    {m.awayName}
                  </div>
                </div>
                <div className="buttons" style={{ justifyContent: 'center' }}>
                  {(NEXT[m.status] ?? []).map(([s, label]) => (
                    <button
                      key={s}
                      type="button"
                      className={s === 'FIRST_HALF' || s === 'FINISHED' || s === 'SECOND_HALF' || s === 'HALF_TIME' ? 'btn btn-primary' : 'btn'}
                      disabled={busy}
                      onClick={() => (destructive(s) ? setConfirm({ status: s, label }) : statusTo(s))}
                    >
                      {label}
                    </button>
                  ))}
                  <ForceStatus current={m.status} onApply={(s) => setConfirm({ status: s, label: `Force “${STATUS_LABEL[s]}”` })} />
                </div>
                {m.status === 'SCHEDULED' && !started && (
                  <p className="muted small" style={{ margin: 0, textAlign: 'center' }}>
                    Kicking off closes predictions for good, even if the kickoff later moves.
                  </p>
                )}
              </div>
            </section>

            <GoalsCard d={d} busy={busy} apply={apply} />
            <ScoresCard d={d} busy={busy} apply={apply} />
          </div>

          <div className="stack" style={{ gap: 20 }}>
            <section className="card" aria-labelledby="pred-h">
              <div className="card-head">
                <h2 id="pred-h">Predictions</h2>
              </div>
              <div className="card-body">
                <dl className="kv">
                  <dt>Predictions</dt>
                  <dd className="num">{d.stats.predictions.toLocaleString()}</dd>
                  <dt>Scoring</dt>
                  <dd>
                    {d.stats.final ? `${d.stats.final} final` : d.stats.provisional ? `${d.stats.provisional} provisional` : d.stats.void ? `${d.stats.void} void` : 'Not started'}
                    {m.revision !== m.scoredRevision ? <span className="pill pill-red" style={{ marginLeft: 6 }}>Behind (rev {m.scoredRevision} of {m.revision})</span> : <span className="pill pill-green" style={{ marginLeft: 6 }}>Up to date · rev {m.revision}</span>}
                  </dd>
                  <dt>Points awarded</dt>
                  <dd className="num">{d.stats.points.toLocaleString()} ({d.stats.exact} exact scores)</dd>
                </dl>
              </div>
            </section>
            <MatchArticles matchId={id} finished={m.status === 'FINISHED'} />
            <ScheduleCard d={d} busy={busy} apply={apply} />
            <LockCard d={d} busy={busy} apply={apply} />

            <section className="card" aria-labelledby="feed-h">
              <div className="card-head">
                <h2 id="feed-h">Data and confirmation</h2>
              </div>
              <div className="card-body stack">
                <dl className="kv">
                  <dt>Source</dt>
                  <dd>{m.provider === 'manual' ? 'Entered by admins' : 'Football data feed'}</dd>
                  <dt>Feed</dt>
                  <dd>{m.feedFrozen ? 'Frozen: feed updates are ignored' : m.provider === 'manual' ? '—' : 'Following the feed'}</dd>
                  <dt>Result</dt>
                  <dd>{m.settledAt ? `Confirmed ${fmtAgo(m.settledAt)}` : m.status === 'FINISHED' ? 'Not confirmed yet' : '—'}</dd>
                </dl>
                <div className="buttons">
                  {m.provider !== 'manual' && (
                    <button type="button" className="btn btn-sm" disabled={busy} onClick={() => apply(() => post(`/admin/matches/${id}/freeze`, { frozen: !m.feedFrozen }), m.feedFrozen ? 'Feed unfrozen' : 'Feed frozen')}>
                      {m.feedFrozen ? 'Hand back to the feed' : 'Freeze feed'}
                    </button>
                  )}
                  {m.provider !== 'manual' && !m.feedFrozen && !m.settledAt && (
                    <button type="button" className="btn btn-sm" disabled={busy} onClick={() => apply(() => post('/admin/feed/sync'), 'Synced with the feed')}>
                      Sync live matches now
                    </button>
                  )}
                  {m.status === 'FINISHED' && (
                    <button type="button" className="btn btn-sm" disabled={busy} onClick={() => apply(() => post(`/admin/matches/${id}/settle`, { settled: !m.settledAt }), m.settledAt ? 'Result reopened' : 'Result confirmed')}>
                      {m.settledAt ? 'Reopen result' : 'Confirm result now'}
                    </button>
                  )}
                </div>
              </div>
            </section>

            <section className="card" aria-labelledby="danger-h">
              <div className="card-head">
                <h2 id="danger-h">Void or delete</h2>
              </div>
              <div className="card-body stack">
                <span className="muted small">Voiding keeps the match but its predictions stop counting. Only a match nobody has predicted can be deleted.</span>
                <div className="buttons">
                  <button type="button" className="btn btn-danger" disabled={busy || m.status === 'CANCELLED'} onClick={() => setConfirm('void')}>
                    Void match
                  </button>
                  <button type="button" className="btn btn-danger" disabled={busy || d.stats.predictions > 0} onClick={() => setConfirm('delete')}>
                    Delete match
                  </button>
                </div>
              </div>
            </section>

            <section className="card" aria-labelledby="hist-h">
              <div className="card-head">
                <h2 id="hist-h">History</h2>
              </div>
              {d.history.length === 0 ? (
                <Empty>No changes yet.</Empty>
              ) : (
                <ol style={{ listStyle: 'none', margin: 0, padding: '4px 16px 12px' }}>
                  {d.history.map((h) => (
                    <li key={h.id} className="row" style={{ padding: '7px 0', borderTop: '1px solid var(--line-2)', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                      <span className="muted small num" style={{ width: 92, flexShrink: 0 }}>
                        {fmtDateTime(h.at)}
                      </span>
                      <span className="small">
                        <strong>{h.action}</strong> <span className="muted">{h.actorUserId ? 'by admin' : 'by system'}</span>
                        {describe(h.after)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        </div>
      </div>

      {confirm === 'void' && (
        <Confirm
          title="Void this match?"
          body="Every prediction for it stops counting (0 points, removed from all totals). Use this for matches abandoned or cancelled for good."
          confirmLabel="Void match"
          danger
          reasonLabel="Reason (kept in the audit log)"
          onClose={() => setConfirm(null)}
          onConfirm={(reason) => apply(() => post(`/admin/matches/${id}/void`, { reason }), 'Match voided')}
        />
      )}
      {confirm === 'delete' && (
        <Confirm
          title="Delete this match?"
          body="It disappears completely. This can't be undone."
          confirmLabel="Delete"
          danger
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await del(`/admin/matches/${id}`);
            await qc.invalidateQueries({ queryKey: ['matches'] });
            navigate({ to: '/matches' });
          }}
        />
      )}
      {confirm && typeof confirm === 'object' && (
        <Confirm
          title={`${confirm.label}?`}
          body={
            confirm.status === 'FINISHED'
              ? 'Points become final for every prediction on this match.'
              : confirm.status === 'POSTPONED'
                ? 'Predictions close until the match is rescheduled.'
                : `The match becomes “${STATUS_LABEL[confirm.status]}”. Points follow at once.`
          }
          confirmLabel={confirm.label}
          danger={confirm.status !== 'FINISHED'}
          onClose={() => setConfirm(null)}
          onConfirm={() => statusTo(confirm.status)}
        />
      )}
    </>
  );
}

function describe(after: unknown): string {
  if (!after || typeof after !== 'object') return '';
  const a = after as Record<string, unknown>;
  if (typeof a.status === 'string') return ` → ${STATUS_LABEL[a.status as MatchStatus] ?? a.status}${a.note ? ` (${a.note})` : ''}`;
  if (typeof a.minute === 'number') return ` · ${a.minute}' ${a.side === 'HOME' ? 'home' : 'away'}${a.cancelled ? ' (disallowed)' : ''}`;
  return '';
}

function ForceStatus({ current, onApply }: { current: MatchStatus; onApply: (s: MatchStatus) => void }) {
  const [s, setS] = useState<MatchStatus | ''>('');
  return (
    <span className="row" style={{ gap: 6 }}>
      <select className="select" aria-label="Force a status" value={s} onChange={(e) => setS(e.target.value as MatchStatus)}>
        <option value="">Force a status…</option>
        {(Object.keys(STATUS_LABEL) as MatchStatus[])
          .filter((x) => x !== current)
          .map((x) => (
            <option key={x} value={x}>
              {STATUS_LABEL[x]}
            </option>
          ))}
      </select>
      <button type="button" className="btn" disabled={!s} onClick={() => s && onApply(s)}>
        Apply
      </button>
    </span>
  );
}

type Apply = (fn: () => Promise<unknown>, ok: string) => Promise<unknown>;

function GoalsCard({ d, busy, apply }: { d: MatchDetail; busy: boolean; apply: Apply }) {
  const m = d.match;
  const [side, setSide] = useState<Side>('HOME');
  const [minute, setMinute] = useState('');
  const [stoppage, setStoppage] = useState('');
  const [kind, setKind] = useState<GoalKind>('GOAL');
  const [playerId, setPlayerId] = useState('');
  const [periodOverride, setPeriod] = useState<Period | ''>('');
  const min = Number(minute);
  const period: Period = periodOverride || (min > 90 ? 'EXTRA_TIME' : min > 45 ? 'SECOND_HALF' : 'FIRST_HALF');
  // The scorer plays for the credited side, or for the other side on an own goal.
  const playsFor: Side = kind === 'OWN_GOAL' ? (side === 'HOME' ? 'AWAY' : 'HOME') : side;
  const squad = d.squads.filter((p) => p.side === playsFor);
  const started = !!m.kickedOffAt;
  return (
    <section className="card" aria-labelledby="goals-h">
      <div className="card-head">
        <h2 id="goals-h">Goals</h2>
        <span className="muted small">The score is calculated from these. Disallowing a goal (VAR) takes its points away at once.</span>
      </div>
      <div className="card-body" style={{ paddingTop: 4 }}>
        {d.goals.length === 0 && <div className="muted" style={{ padding: '10px 0' }}>No goals yet.</div>}
        {d.goals.map((g) => (
          <div key={g.id} className={`goal-row${g.cancelled ? ' cancelled' : ''}`}>
            <span className="display num" style={{ fontSize: 18 }}>
              {g.minute}
              {g.stoppage ? `+${g.stoppage}` : ''}'
            </span>
            <span className="who">
              <strong>{g.side === 'HOME' ? m.homeName : m.awayName}</strong> · {g.playerName ?? 'scorer unknown'}
              {g.kind !== 'GOAL' && <span className="muted"> ({KIND_LABEL[g.kind]})</span>}
              <span className="muted small"> · {PERIOD_LABEL[g.period]}</span>
              {g.cancelled && <span className="pill pill-amber" style={{ marginLeft: 6 }}>Disallowed</span>}
            </span>
            <span className="row" style={{ gap: 6 }}>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => apply(() => patch(`/admin/matches/${m.id}/goals/${g.id}`, { cancelled: !g.cancelled }), g.cancelled ? 'Goal restored' : 'Goal disallowed')}>
                {g.cancelled ? 'Restore' : 'Disallow (VAR)'}
              </button>
              <button type="button" className="btn btn-sm btn-ghost" aria-label={`Delete the ${g.minute}' goal`} disabled={busy} onClick={() => apply(() => del(`/admin/matches/${m.id}/goals/${g.id}`), 'Goal deleted')}>
                Delete
              </button>
            </span>
          </div>
        ))}
        {started ? (
          <form
            className="stack"
            style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14, marginTop: 6 }}
            onSubmit={async (e) => {
              e.preventDefault();
              const r = await apply(
                () =>
                  post(`/admin/matches/${m.id}/goals`, {
                    side,
                    kind,
                    period,
                    minute: min,
                    stoppage: stoppage ? Number(stoppage) : 0,
                    playerId: playerId ? Number(playerId) : null,
                  }),
                'Goal added',
              );
              if (r) {
                setMinute('');
                setStoppage('');
                setPlayerId('');
                setPeriod('');
              }
            }}
          >
            <div className="row" style={{ alignItems: 'flex-end' }}>
              <div className="field">
                <span>Credited to</span>
                <Seg label="Credited to" value={side} onChange={(v) => { setSide(v); setPlayerId(''); }} options={[['HOME', m.homeName], ['AWAY', m.awayName]]} />
              </div>
              <Field label="Minute">
                <input className="input" type="number" min={1} max={130} required style={{ width: 80 }} value={minute} onChange={(e) => setMinute(e.target.value)} />
              </Field>
              <Field label="+ stoppage">
                <input className="input" type="number" min={0} max={30} style={{ width: 80 }} value={stoppage} onChange={(e) => setStoppage(e.target.value)} />
              </Field>
              <Field label="Period">
                <select className="select" value={periodOverride} onChange={(e) => setPeriod(e.target.value as Period | '')}>
                  <option value="">Auto ({PERIOD_LABEL[period]})</option>
                  <option value="FIRST_HALF">First half</option>
                  <option value="SECOND_HALF">Second half</option>
                  <option value="EXTRA_TIME">Extra time</option>
                </select>
              </Field>
              <Field label="Type">
                <select className="select" value={kind} onChange={(e) => { setKind(e.target.value as GoalKind); setPlayerId(''); }}>
                  <option value="GOAL">Goal</option>
                  <option value="PENALTY_GOAL">Penalty</option>
                  <option value="OWN_GOAL">Own goal</option>
                </select>
              </Field>
              <Field label={kind === 'OWN_GOAL' ? `Scorer (${playsFor === 'HOME' ? m.homeName : m.awayName})` : 'Scorer'}>
                <select className="select" value={playerId} onChange={(e) => setPlayerId(e.target.value)} style={{ minWidth: 180 }}>
                  <option value="">Not known yet</option>
                  {squad.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.position ? ` · ${p.position}` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <button className="btn btn-primary" disabled={busy || !minute}>
                Add goal
              </button>
            </div>
            {squad.length === 0 && <span className="muted small">This squad has no players yet: add them under Teams and squads, or record the goal with the scorer unknown.</span>}
          </form>
        ) : (
          <p className="muted small">Kick the match off to record goals.</p>
        )}
      </div>
    </section>
  );
}

function ScorePair({ label, value, onChange }: { label: string; value: [string, string]; onChange: (v: [string, string]) => void }) {
  return (
    <div className="field">
      <span>{label}</span>
      <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
        <input className="input" aria-label={`${label} home`} type="number" min={0} max={99} style={{ width: 64 }} value={value[0]} onChange={(e) => onChange([e.target.value, value[1]])} />
        <span>–</span>
        <input className="input" aria-label={`${label} away`} type="number" min={0} max={99} style={{ width: 64 }} value={value[1]} onChange={(e) => onChange([value[0], e.target.value])} />
      </span>
    </div>
  );
}

function ScoresCard({ d, busy, apply }: { d: MatchDetail; busy: boolean; apply: Apply }) {
  const m = d.match;
  const s = (a: number | null, b: number | null): [string, string] => [a === null ? '' : String(a), b === null ? '' : String(b)];
  const [ht, setHt] = useState(s(m.htHome, m.htAway));
  const [rt, setRt] = useState(s(m.rtHome, m.rtAway));
  const [aet, setAet] = useState(s(m.aetHome, m.aetAway));
  const [pen, setPen] = useState(s(m.penHome, m.penAway));
  useEffect(() => {
    setHt(s(m.htHome, m.htAway));
    setRt(s(m.rtHome, m.rtAway));
    setAet(s(m.aetHome, m.aetAway));
    setPen(s(m.penHome, m.penAway));
  }, [m.htHome, m.htAway, m.rtHome, m.rtAway, m.aetHome, m.aetAway, m.penHome, m.penAway]);
  const toScore = (v: [string, string]) => (v[0] === '' && v[1] === '' ? null : { home: Number(v[0]), away: Number(v[1]) });
  return (
    <section className="card" aria-labelledby="scores-h">
      <div className="card-head">
        <h2 id="scores-h">Correct the score by hand</h2>
        <span className="muted small">For matches without goal events. Leave a pair empty to clear it.</span>
      </div>
      <div className="card-body row" style={{ alignItems: 'flex-end', gap: 16 }}>
        <ScorePair label="Half-time" value={ht} onChange={setHt} />
        <ScorePair label="90 minutes" value={rt} onChange={setRt} />
        <ScorePair label="After extra time" value={aet} onChange={setAet} />
        <ScorePair label="Shoot-out" value={pen} onChange={setPen} />
        <button
          type="button"
          className="btn"
          disabled={busy || !m.kickedOffAt}
          onClick={() => apply(() => put(`/admin/matches/${m.id}/scores`, { halfTime: toScore(ht), regularTime: toScore(rt), afterExtraTime: toScore(aet), penalties: toScore(pen) }), 'Score saved')}
        >
          Save score
        </button>
      </div>
    </section>
  );
}

function ScheduleCard({ d, busy, apply }: { d: MatchDetail; busy: boolean; apply: Apply }) {
  const m = d.match;
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<{ settings: GameSettings }>('/admin/settings') });
  const rounds = useQuery({ queryKey: ['rounds', 'all'], queryFn: () => get<{ rounds: Round[] }>(`/admin/rounds${qs({})}`) });
  const comps = useQuery({ queryKey: ['competitions'], queryFn: () => get<{ competitions: Competition[] }>('/admin/competitions') });
  const [kickoff, setKickoff] = useState(toLocalInput(m.kickoffAt));
  const [tbc, setTbc] = useState(!m.kickoffConfirmed);
  const [roundId, setRoundId] = useState(m.roundId ? String(m.roundId) : '');
  const [competitionId, setCompetitionId] = useState(String(m.competitionId));
  useEffect(() => {
    setKickoff(toLocalInput(m.kickoffAt));
    setTbc(!m.kickoffConfirmed);
    setRoundId(m.roundId ? String(m.roundId) : '');
    setCompetitionId(String(m.competitionId));
  }, [m.kickoffAt, m.kickoffConfirmed, m.roundId, m.competitionId]);
  const played = !!m.kickedOffAt;
  return (
    <section className="card" aria-labelledby="sched-h">
      <div className="card-head">
        <h2 id="sched-h">Schedule</h2>
      </div>
      <form
        className="card-body stack"
        onSubmit={(e) => {
          e.preventDefault();
          const body: Record<string, unknown> = { kickoffAt: fromLocalInput(kickoff), kickoffConfirmed: !tbc };
          if ((roundId ? Number(roundId) : null) !== m.roundId) body.roundId = roundId ? Number(roundId) : null;
          if (Number(competitionId) !== m.competitionId) body.competitionId = Number(competitionId);
          void apply(() => patch(`/admin/matches/${m.id}`, body), 'Schedule saved');
        }}
      >
        <Field label="Kickoff" hint={`Moving a locked match more than ${settings.data?.settings.reopenIfMovedLaterByHours ?? 24} h later reopens it (unless it has started).`}>
          <input className="input" type="datetime-local" value={kickoff} onChange={(e) => setKickoff(e.target.value)} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={tbc} onChange={(e) => setTbc(e.target.checked)} />
          Time to be confirmed
        </label>
        <Field label="Round" hint={played ? 'Played matches stay in their round' : undefined}>
          <select className="select" value={roundId} disabled={played} onChange={(e) => setRoundId(e.target.value)}>
            <option value="">No round</option>
            {rounds.data?.rounds.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} ({r.season})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Competition" hint={played ? 'Played matches keep their competition' : undefined}>
          <select className="select" value={competitionId} disabled={played} onChange={(e) => setCompetitionId(e.target.value)}>
            {comps.data?.competitions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.season}
              </option>
            ))}
          </select>
        </Field>
        <button className="btn" disabled={busy}>
          Save schedule
        </button>
      </form>
    </section>
  );
}

function LockCard({ d, busy, apply }: { d: MatchDetail; busy: boolean; apply: Apply }) {
  const m = d.match;
  const [lockAt, setLockAt] = useState(toLocalInput(m.lockAt));
  useEffect(() => setLockAt(toLocalInput(m.lockAt)), [m.lockAt]);
  const open = m.published && m.status === 'SCHEDULED' && m.kickoffConfirmed && !m.kickedOffAt && new Date(m.lockAt).getTime() > Date.now();
  return (
    <section className="card" aria-labelledby="lock-h">
      <div className="card-head">
        <h2 id="lock-h">Prediction lock</h2>
        <span className={open ? 'pill pill-green' : 'pill'}>{open ? 'Open' : 'Closed'}</span>
      </div>
      <div className="card-body stack">
        <span className="muted small">
          Locks {fmtDateTime(m.lockAt)}
          {m.lockOverridden ? ' (set by hand)' : " (automatic, from the competition's lock time)"}.{m.kickedOffAt ? ' The match has started, so it can never reopen.' : ''}
        </span>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <Field label="Lock at">
            <input className="input" type="datetime-local" value={lockAt} onChange={(e) => setLockAt(e.target.value)} />
          </Field>
          <button type="button" className="btn" disabled={busy} onClick={() => apply(() => post(`/admin/matches/${m.id}/lock`, { lockAt: fromLocalInput(lockAt) }), 'Lock time set')}>
            Set
          </button>
        </div>
        <div className="buttons">
          <button type="button" className="btn btn-sm" disabled={busy || !open} onClick={() => apply(() => post(`/admin/matches/${m.id}/lock`, { lockAt: new Date().toISOString() }), 'Predictions closed')}>
            Close predictions now
          </button>
          {m.lockOverridden && (
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => apply(() => post(`/admin/matches/${m.id}/lock`, { lockAt: null }), 'Lock back to automatic')}>
              Back to automatic
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/** The match's preview and report: open them, or have the AI write them. */
function MatchArticles({ matchId, finished }: { matchId: number; finished: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ai = useAiStatus();
  const q = useQuery({
    queryKey: ['articles', { matchId }],
    queryFn: () => get<{ articles: ArticleAdmin[] }>(`/admin/articles${qs({ matchId })}`),
    refetchInterval: (query) => (query.state.data?.articles.some((a) => a.aiState === 'QUEUED' || a.aiState === 'RUNNING') ? 3000 : false),
  });
  const { busy, run } = useAction();
  const list = q.data?.articles ?? [];
  const has = (k: string) => list.find((a) => a.kind === k);
  const generate = (kind: 'PREVIEW' | 'REPORT') =>
    run(async () => {
      await post(`/admin/matches/${matchId}/articles`, { kind });
      await qc.invalidateQueries({ queryKey: ['articles'] });
    }, kind === 'PREVIEW' ? 'The AI is writing the preview' : 'The AI is writing the report');
  return (
    <section className="card" aria-labelledby="art-h">
      <div className="card-head">
        <h2 id="art-h">Articles</h2>
      </div>
      {list.length > 0 && (
        <table className="table">
          <tbody>
            {list.map((a) => (
              <tr key={a.id} className="clickable" onClick={() => navigate({ to: '/articles/$articleId', params: { articleId: String(a.id) } })}>
                <td>
                  <span className="muted small">{ARTICLE_KIND[a.kind]}</span>
                  <div>{a.title}</div>
                </td>
                <td className="right">
                  <ArticleState a={a} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="card-body stack">
        {ai.data && !ai.data.configured && <span className="muted small">AI writing is off (no ANTHROPIC_API_KEY); requests wait until it's set.</span>}
        <div className="buttons">
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void generate('PREVIEW')}>
            {has('PREVIEW') ? 'Rewrite preview with AI' : 'Preview with AI'}
          </button>
          <button type="button" className="btn btn-sm" disabled={busy || !finished} title={finished ? '' : 'After full time'} onClick={() => void generate('REPORT')}>
            {has('REPORT') ? 'Rewrite report with AI' : 'Report with AI'}
          </button>
          <Link to="/articles/new" search={{ matchId, kind: has('PREVIEW') ? (has('REPORT') ? 'NEWS' : 'REPORT') : 'PREVIEW' }} className="btn btn-sm">
            Write one by hand
          </Link>
        </div>
      </div>
    </section>
  );
}
